import { agentEditorCopy } from '@/copy'
import type { ScheduleEntry } from '@/types/editor'
import { Button } from '@/ui/Button/Button'
import { Field, Input, Textarea } from '@/ui/Field/Field'
import { Icon } from '@/ui/Icon/Icon'
import { Grid } from '@/ui/layout/Grid'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import styles from './ScheduleSection.module.css'

interface ScheduleSectionProps {
  entries: ScheduleEntry[]
  onChange: (v: ScheduleEntry[]) => void
}

export function ScheduleSection({ entries, onChange }: ScheduleSectionProps) {
  function add() {
    onChange([
      ...entries,
      {
        schedule: '',
        task: '',
        active_hours: '',
        timezone: '',
      },
    ])
  }
  function remove(i: number) {
    onChange(entries.filter((_, idx) => idx !== i))
  }
  function setField(i: number, k: keyof ScheduleEntry, v: string) {
    const next = entries.map((e, idx) => (idx === i ? { ...e, [k]: v } : e))
    onChange(next)
  }

  return (
    <Stack gap="space-16">
      <SectionHeader>{agentEditorCopy.schedule.heading}</SectionHeader>
      <Stack gap="space-24">
        {entries.map((e, i) => (
          <fieldset
            // biome-ignore lint/suspicious/noArrayIndexKey: list has no stable id
            key={i}
            className={styles.entry}
          >
            <legend className={styles.legend}>
              <Inline gap="space-8" justify="between" align="baseline">
                <Text tone="text-ghost">
                  {agentEditorCopy.schedule.entry(i + 1)}
                </Text>
                <Button
                  variant="ghost"
                  ground="surface"
                  onClick={() => remove(i)}
                >
                  {agentEditorCopy.schedule.remove}
                </Button>
              </Inline>
            </legend>
            <Field
              id={`sched-cron-${i}`}
              label={agentEditorCopy.schedule.cronLabel}
              hint={agentEditorCopy.schedule.requiredHint}
              required
            >
              <Input
                value={e.schedule}
                onChange={(ev) => setField(i, 'schedule', ev.target.value)}
                placeholder={agentEditorCopy.schedule.cronPlaceholder}
                mono
              />
            </Field>
            <Field
              id={`sched-task-${i}`}
              label={agentEditorCopy.schedule.taskLabel}
              hint={agentEditorCopy.schedule.requiredHint}
              required
            >
              <Textarea
                value={e.task}
                onChange={(ev) => setField(i, 'task', ev.target.value)}
                rows={2}
                placeholder=""
              />
            </Field>
            <Grid columns={1} mdColumns={2} gap="space-16">
              <Field
                id={`sched-hours-${i}`}
                label={agentEditorCopy.schedule.activeHours}
              >
                <Input
                  value={e.active_hours}
                  onChange={(ev) =>
                    setField(i, 'active_hours', ev.target.value)
                  }
                  placeholder={agentEditorCopy.schedule.activeHoursPlaceholder}
                />
              </Field>
              <Field
                id={`sched-tz-${i}`}
                label={agentEditorCopy.schedule.timezone}
              >
                <Input
                  value={e.timezone}
                  onChange={(ev) => setField(i, 'timezone', ev.target.value)}
                  placeholder={agentEditorCopy.schedule.timezonePlaceholder}
                />
              </Field>
            </Grid>
          </fieldset>
        ))}
      </Stack>
      <Button variant="ghost" ground="surface" onClick={add}>
        <Icon name="plus" size="icon-embedded" />
        {agentEditorCopy.schedule.add}
      </Button>
    </Stack>
  )
}
